/**
 * g.copy - copy to clipboard, with the house feedback (BSP's ask, Sep
 * 2026: BIDs/PIDs pasted into Archicad and mails all day).
 *
 * Programmatic:  await g.copy('01.02.001')            -> true | false
 *                await g.copy(text, { host: btn })    -> flips the host's
 *                icon to a check for a moment (the feedback below)
 *
 * Declarative (delegated, no wiring): a button carrying `copy="text"`
 * copies that text; `copy-target="selector"` copies the target element's
 * value (a field) or text. The host is the button itself:
 *
 *   <span class="copyable">
 *     <span class="code">01.02.001</span>
 *     <button type="button" class="btn-copy" copy="01.02.001" title="Copy" aria-label="Copy">
 *       <i class="ri-file-copy-line" aria-hidden="true"></i>
 *     </button>
 *   </span>
 *
 * Feedback: the host gets `.is-copied` and its <i> glyph becomes
 * ri-check-line for 1.5 s (tooltip/aria-label read "Copied"); no toast -
 * a value copied twenty times an hour must not stack twenty toasts. A
 * failed copy (no clipboard access - an insecure context, a denied
 * permission) raises ONE error toast and returns false.
 *
 * Full reference: docs/libs/genus.md "Copy to clipboard".
 */
import { translate } from './translate.js';
import { error } from './notify.js';

const FEEDBACK_MS = 1500;

async function write(text) {
	if (navigator.clipboard && window.isSecureContext) {
		try {
			await navigator.clipboard.writeText(text);
			return true;
		} catch { /* denied or unavailable - the fallback below */ }
	}
	// legacy road: a transient textarea + execCommand (still the only way
	// on an insecure origin)
	try {
		const ta = document.createElement('textarea');
		ta.value = text;
		ta.setAttribute('readonly', '');
		ta.style.cssText = 'position:fixed;top:0;left:0;opacity:0;pointer-events:none;';
		document.body.appendChild(ta);
		ta.select();
		const ok = document.execCommand('copy');
		ta.remove();
		return ok;
	} catch {
		return false;
	}
}

function feedback(host) {
	if (!(host instanceof Element)) return;
	const icon = host.querySelector('i');
	const glyph = icon && [...icon.classList].find((c) => c.startsWith('ri-'));
	// the template's lazy tooltips move `title` to data-bs-original-title
	// and own the visible text from then on - update through the instance
	const tip = window.bootstrap && window.bootstrap.Tooltip ? window.bootstrap.Tooltip.getInstance(host) : null;
	const title = host.getAttribute('title') ?? host.getAttribute('data-bs-original-title');
	const label = host.getAttribute('aria-label');
	// never tip.setContent() on a SHOWN tooltip: it re-runs show() (a new
	// fade) and a row removed mid-fade leaves Bootstrap's complete() to run
	// on a disposed instance (a null-config throw). Write the text where
	// the next show reads it (data-bs-original-title) and into the live
	// tip node directly - no fade, nothing queued.
	const say = (text) => {
		if (tip) {
			host.setAttribute('data-bs-original-title', text);
			const inner = tip.tip && tip.tip.querySelector('.tooltip-inner');
			if (inner) inner.textContent = text;
		} else if (host.hasAttribute('title')) host.setAttribute('title', text);
		if (label !== null) host.setAttribute('aria-label', text);
	};
	clearTimeout(host._copyTimer);
	host.classList.add('is-copied');
	if (icon && glyph) icon.classList.replace(glyph, 'ri-check-line');
	if (title !== null || label !== null) say(translate('Copied'));
	host._copyTimer = setTimeout(() => {
		host.classList.remove('is-copied');
		// restore only what is still ours - content the page rewrote is kept
		if (icon && glyph && icon.classList.contains('ri-check-line')) icon.classList.replace('ri-check-line', glyph);
		if (title !== null) say(title);
		else if (label !== null) host.setAttribute('aria-label', label);
	}, FEEDBACK_MS);
}

export async function copy(text, { host = null } = {}) {
	const ok = await write(text == null ? '' : String(text));
	if (ok) feedback(host);
	else error(translate('Could not copy to the clipboard.'));
	return ok;
}

// The value a [copy] / [copy-target] button stands for.
function valueOf(btn) {
	if (btn.hasAttribute('copy') && btn.getAttribute('copy') !== '') return btn.getAttribute('copy');
	const sel = btn.getAttribute('copy-target');
	const el = sel ? document.querySelector(sel) : null;
	if (!el) return '';
	if ('value' in el && typeof el.value === 'string' && el.matches('input, textarea, select')) return el.value;
	return (el.textContent || '').trim();
}

let delegated = false;
export function initCopy() {
	if (delegated) return;
	delegated = true;
	document.addEventListener('click', (e) => {
		const btn = e.target.closest('[copy], [copy-target]');
		if (!btn) return;
		e.preventDefault();
		e.stopPropagation(); // a copy inside a clickable row must not toggle the row
		copy(valueOf(btn), { host: btn });
	});
}
